import React, { useEffect, useState } from 'react'
import { Badge, Button, OverlayTrigger, Popover, Spinner, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'
import { formatDateTime } from '../../utils/dateFormatter'
import { toastService } from '../../services/toast.service'
import { DocumentVersion } from '../../types'
import { ConfirmDialog } from '../common/ConfirmDialog'
import './DocumentVersions.scss'

interface DocumentVersionsProps {
  documentId: number
  refreshKey?: number
  onRestored?: (restoredVersion: DocumentVersion) => void | Promise<void>
}

const DocumentVersions: React.FC<DocumentVersionsProps> = ({ documentId, refreshKey, onRestored }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [restoring, setRestoring] = useState(false)
  const [versions, setVersions] = useState<DocumentVersion[]>([])
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)
  const [restoreTarget, setRestoreTarget] = useState<DocumentVersion | null>(null)

  const loadVersions = async () => {
    try {
      setLoading(true)
      const data = await documentService.getVersions(documentId)
      setVersions(data)
    } catch (error) {
      toastService.showError(t('error_loading_versions'), error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadVersions()
  }, [documentId, refreshKey])

  const handlePreview = (version: DocumentVersion) => {
    const url = documentService.getPreviewUrl(version.id)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleDownload = async (version: DocumentVersion, archive: boolean) => {
    const filename = archive ? version.archived_file_name || undefined : version.original_file_name || undefined
    try {
      await documentService.downloadDocumentFile(version.id, !archive, filename, archive)
    } catch (error) {
      toastService.showError(t('error_downloading_document'), error)
    }
  }

  const confirmRestore = (version: DocumentVersion) => {
    setRestoreTarget(version)
    setShowRestoreConfirm(true)
  }

  const executeRestore = async () => {
    const target = restoreTarget
    if (!target) return

    setShowRestoreConfirm(false)
    setRestoreTarget(null)

    try {
      setRestoring(true)
      const restoredVersion = await documentService.restoreVersion(documentId, target.id)
      if (restoredVersion.id === documentId) {
        await loadVersions()
      }
      await onRestored?.(restoredVersion)
    } catch (error) {
      toastService.showError(t('error_restoring_version'), error)
    } finally {
      setRestoring(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex">
        <Spinner animation="border" size="sm" role="status" className="fw-normal" />
      </div>
    )
  }

  return (
    <div className="document-versions">
      <Table responsive hover size="sm">
        <thead>
          <tr>
            <th>{t('version')}</th>
            <th>{t('date')}</th>
            <th>{t('user')}</th>
            <th>{t('comment')}</th>
            <th className="text-end">{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {versions.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-muted">
                {t('no_entries_found')}
              </td>
            </tr>
          ) : (
            versions.map((version) => (
              <tr key={version.id}>
                <td className="text-nowrap">
                  <span className="me-2">V{version.version_number}</span>
                </td>
                <td className="text-nowrap">{formatDateTime(version.added)}</td>
                <td>{version.version_created_by?.username || t('system')}</td>
                <td>{version.version_comment || '-'}</td>
                <td className="text-end">
                  <div className="document-versions__actions">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => confirmRestore(version)}
                      style={{ display: version.is_current ? 'none' : 'inline-flex' }}
                      disabled={restoring}
                    >
                      <i className="bi bi-arrow-counterclockwise"></i>&nbsp;{t('restore')}
                    </Button>
                    {version.is_current && (
                      <Badge bg="primary" className="d-inline-flex align-items-center">
                        {t('current')}
                      </Badge>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePreview(version)}
                      disabled={!version.has_archive_version}
                      title={t('preview')}
                    >
                      <i className="bi bi-eye"></i>
                    </Button>
                    <OverlayTrigger
                      trigger="click"
                      placement="bottom"
                      rootClose
                      container={document.body}
                      overlay={
                        <Popover className="shadow">
                          <Popover.Body className="d-grid gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleDownload(version, true)}
                              disabled={!version.has_archive_version}
                            >
                              {t('download_archive')}
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleDownload(version, false)}>
                              {t('download_original')}
                            </Button>
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <Button variant="outline-secondary" size="sm">
                        <i className="bi bi-download"></i>
                      </Button>
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <ConfirmDialog
        show={showRestoreConfirm}
        onCancel={() => setShowRestoreConfirm(false)}
        title={t('restore_version_confirm')}
        message={t('restore_version_confirm_message')}
        confirmText={t('restore')}
        variant="warning"
        onConfirm={executeRestore}
        hideCancelButton={restoring}
      />
    </div>
  )
}

export default DocumentVersions
